import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { MessagesService, Conversation } from '../services/messages.service';
import { WebSocketService } from '../services/websocket.service';
import { AuthService } from '../services/auth.service';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';

@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent],
  templateUrl: './messages.component.html',
  styleUrl: './messages.component.css',
})
export class MessagesComponent implements OnInit, OnDestroy {
  conversations: Conversation[] = [];
  loading = true;
  error: string | null = null;
  currentUserId: number | null = null;

  connectionState: 'disconnected' | 'connecting' | 'connected' | null = null;
  onlineUsers = new Set<string>();
  totalUnreadCount = 0;
  isRefreshing = false;
  private hasInitializedConnection = false;
  private showConnectionBanner = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private messagesService: MessagesService,
    private webSocketService: WebSocketService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const userSub = this.authService.currentUser$.subscribe((user) => {
      this.currentUserId = user?.id || null;
    });
    this.subscriptions.push(userSub);
    this.connectionState = this.webSocketService.getCurrentConnectionState();

    const connectionSub = this.webSocketService
      .getConnectionState()
      .subscribe((state) => {
        this.connectionState = state;
        if (!this.hasInitializedConnection) {
          this.hasInitializedConnection = true;
        }
      });
    this.subscriptions.push(connectionSub);

    const conversationsSub = this.messagesService.conversations$.subscribe(
      (conversations) => {
        if (conversations.length > 0) {
          this.conversations = [...conversations].sort(
            (a, b) =>
              new Date(b.last_message_at).getTime() -
              new Date(a.last_message_at).getTime()
          );
          this.loading = false;
        }
      }
    );
    this.subscriptions.push(conversationsSub);

    const onlineUsersSub = this.messagesService.onlineUsers$.subscribe(
      (users) => {
        this.onlineUsers = users;
      }
    );
    this.subscriptions.push(onlineUsersSub);

    const unreadSub = this.messagesService.unreadCount$.subscribe((count) => {
      this.totalUnreadCount = count;
    });
    this.subscriptions.push(unreadSub);

    this.loadConversations();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadConversations(): void {
    this.loading = true;
    this.error = null;

    this.messagesService.getUserConversations().subscribe({
      next: (response) => {
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading conversations:', error);
        this.error = error.message || 'Failed to load conversations';
        this.loading = false;
      },
    });
  }

  refreshConversations(): void {
    if (this.isRefreshing) return;

    this.isRefreshing = true;
    this.messagesService.getUserConversations().subscribe({
      next: () => {
        this.isRefreshing = false;
        this.messagesService.refreshUnreadCount();
      },
      error: (error) => {
        console.error('Error refreshing conversations:', error);
        this.isRefreshing = false;
      },
    });
  }

  openConversation(conversation: Conversation): void {
    if (conversation.unread_count > 0) {
      this.messagesService.markMessagesAsRead(conversation.id).subscribe({
        next: () => {
          conversation.unread_count = 0;
        },
        error: (error) => {
          console.error('Error marking as read:', error);
        },
      });
    }

    this.router.navigate(['/messages', conversation.id]);
  }

  deleteConversation(conversation: Conversation, event: Event): void {
    event.stopPropagation();

    if (
      confirm(
        'Are you sure you want to delete this conversation? This will only delete it for you, not for the other person.'
      )
    ) {
      this.messagesService.deleteConversation(conversation.id).subscribe({
        next: () => {
          this.conversations = this.conversations.filter(
            (c) => c.id !== conversation.id
          );
          this.messagesService.refreshUnreadCount();
        },
        error: (error) => {
          console.error('Error deleting conversation:', error);
          alert('Failed to delete conversation. Please try again.');
        },
      });
    }
  }

  getOtherUserName(conversation: Conversation): string {
    return conversation.other_user_name || 'Unknown User';
  }

  getListingTitle(conversation: Conversation): string {
    return conversation.listing_title || 'Unknown Listing';
  }

  getLastMessagePreview(conversation: Conversation): string {
    return conversation.last_message_preview || 'No messages yet';
  }

  formatTimestamp(dateString: string): string {
    return this.messagesService.getTimestamp(dateString);
  }

  formatPrice(price: number): string {
    return this.messagesService.formatPrice(price);
  }

  getCategoryFromTableName(tableName: string): string {
    const categoryMap: { [key: string]: string } = {
      attributes_automobile: 'Vehicles',
      attributes_motorcycle: 'Vehicles',
      attributes_commercial_vehicles: 'Vehicles',
      attributes_oldtimer: 'Vehicles',
      attributes_nautical: 'Vehicles',
      attributes_spare_parts: 'Vehicles',
      attributes_campers_caravans: 'Vehicles',
      attributes_crashed_cars: 'Vehicles',
      attributes_construction: 'Vehicles',
      attributes_agriculture: 'Vehicles',
      attributes_house_sale: 'Real Estate',
      attributes_house_rent: 'Real Estate',
      attributes_apartments_sale: 'Real Estate',
      attributes_apartments_rent: 'Real Estate',
      attributes_land_sale: 'Real Estate',
      attributes_land_rent: 'Real Estate',
      attributes_business_office_sale: 'Real Estate',
      attributes_business_office_rent: 'Real Estate',
      attributes_garage_sale: 'Real Estate',
      attributes_garage_rent: 'Real Estate',
      attributes_luxury_real_estate_sale: 'Real Estate',
      attributes_luxury_real_estate_rent: 'Real Estate',
      attributes_room_rent: 'Real Estate',
      attributes_roommate: 'Real Estate',
      attributes_home: 'Marketplace',
      attributes_electronics: 'Marketplace',
      attributes_sport: 'Marketplace',
      attributes_children: 'Marketplace',
      attributes_head_to_toe: 'Marketplace',
      attributes_machines_tools: 'Marketplace',
      attributes_media: 'Marketplace',
      attributes_musical_instruments: 'Marketplace',
      attributes_literature: 'Marketplace',
      attributes_pets: 'Marketplace',
      attributes_other: 'Marketplace',
      attributes_giving_away: 'Marketplace',
      attributes_professional: 'Jobs',
      attributes_home_maintenance: 'Jobs',
      attributes_personal_care_wellness: 'Jobs',
      attributes_events_entertainment: 'Jobs',
      attributes_education_training: 'Jobs',
      attributes_transport_delivery: 'Jobs',
    };
    return categoryMap[tableName] || 'Unknown';
  }

  hasConversations(): boolean {
    return this.conversations.length > 0;
  }

  getTotalUnreadCount(): number {
    return this.totalUnreadCount;
  }

  trackByConversation(index: number, conversation: Conversation): string {
    return conversation.id;
  }

  isUserOnline(userId: string): boolean {
    return this.messagesService.isUserOnline(userId);
  }

  isOtherUserOnline(conversation: Conversation): boolean {
    return this.isUserOnline(conversation.other_user_id);
  }

  getConnectionStatusText(): string {
    switch (this.connectionState) {
      case 'connecting':
        return 'Connecting to real-time messaging...';
      case 'connected':
        return 'Connected - Real-time messaging active';
      case 'disconnected':
        return 'Disconnected - Messages may be delayed';
      default:
        return '';
    }
  }

  isConnectionHealthy(): boolean {
    return this.connectionState === 'connected';
  }

  shouldShowConnectionBanner(): boolean {
    return this.showConnectionBanner;
  }

  retryConnection(): void {
    this.messagesService.retryConnection();
  }

  getConversationAge(conversation: Conversation): string {
    const lastMessageDate = new Date(conversation.last_message_at);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - lastMessageDate.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) {
      return 'Active now';
    } else if (diffInHours < 24) {
      return `Active ${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Active ${diffInDays}d ago`;
    }
  }

  hasRecentActivity(conversation: Conversation): boolean {
    const lastMessageDate = new Date(conversation.last_message_at);
    const now = new Date();
    const diffInHours =
      (now.getTime() - lastMessageDate.getTime()) / (1000 * 60 * 60);
    return diffInHours < 24;
  }

  getUnreadBadgeColor(count: number): string {
    if (count > 10) return 'badge-high';
    if (count > 5) return 'badge-medium';
    return 'badge-normal';
  }

  onConversationKeyDown(
    event: KeyboardEvent,
    conversation: Conversation
  ): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openConversation(conversation);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.deleteConversation(conversation, event);
    }
  }

  getConversationAriaLabel(conversation: Conversation): string {
    const userName = this.getOtherUserName(conversation);
    const unreadText =
      conversation.unread_count > 0
        ? `, ${conversation.unread_count} unread message${
            conversation.unread_count > 1 ? 's' : ''
          }`
        : '';
    const onlineText = this.isOtherUserOnline(conversation) ? ', online' : '';
    const timeText = `, last message ${this.formatTimestamp(
      conversation.last_message_at
    )}`;

    return `Conversation with ${userName}${unreadText}${onlineText}${timeText}`;
  }

  retry(): void {
    this.error = null;
    this.loadConversations();
  }

  forceReconnect(): void {
    this.messagesService.retryConnection();
  }

  getWebSocketStats(): any {
    return this.webSocketService.getStats();
  }
}
